import { User, UserService } from '@hub-services/user.service';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { getUser } from '@hub-store/user/user.selectors';
import { setUser } from '@hub-store/user/user.actions';
import { concatMap, filter, forkJoin, map, of, take, tap } from 'rxjs';
import { RoleType } from '../../admin/role-type.enum';
import { updateSessionUserInStore } from '@hub-store/session/session.actions';
import { getSessionData } from '@hub-store/session/session.selectors';
import { Partner } from '@hub-services/partner';
import { setPartner } from '@hub-store/selected-partner/selected-partner.actions';
import { PartnerType } from '../../admin/admin-partners/partner-type.enum';
import { PartnersService } from '@hub-services/partners.service';
import { UserflowService } from '@hub-services/userflow.service';

@Injectable({ providedIn: 'root' })
export class UserResolve  {
  constructor(
    private userService: UserService,
    private store: Store,
    private partnersService: PartnersService,
    private userflowService: UserflowService
  ) { }

  resolve() {
    return this.store.select(getUser)
      .pipe(
        take(1),
        concatMap(user => user?.role
          ? of(user)
          : this.userService
            .updateUser()
            .pipe(
              // TODO(strict-typing): updateUser() resolves to null when the user is authenticated
              // but has no recognized UMS role (see UserService.setUser's invariant comment).
              // Filter it out before it reaches getPermissionCommunityParticipant/setFullstoryUser,
              // which dereference `.role` and would throw on null. Pre-existing gap: the resolver
              // previously let `null` flow through the User-typed taps below and would have crashed
              // there. Real fix: a guards-focused chunk should redirect to /access-denied here
              // instead of silently never emitting, mirroring AuthGuard.checkPermisions.
              filter((userData): userData is User => userData !== null),
              tap((userData: User) => this.store.dispatch(setUser({
                user: {
                  ...userData,
                  isCommunityParticipant: this.userService.getPermissionCommunityParticipant(userData),
                  isCollaborator: userData.role === RoleType.COLLABORATOR,
                }
              }))),
              tap((userData: User) => {
                this.userService.setFullstoryUser(userData);
                if (!this.userflowService.isIdentified) {
                  this.userflowService.identify(userData);
                }
                this.store.dispatch(updateSessionUserInStore({ user: {
                  id: userData.id,
                  email: userData.email}}));
                }),
              concatMap((userData: User) => this.seedInitialPartnerWhenSessionIsEmpty(userData)),
            )));
  }

  private seedInitialPartnerWhenSessionIsEmpty(userData: User) {
    return this.store.select(getSessionData).pipe(
      take(1),
      concatMap((sessionData) => {
        if (sessionData?.partner_id) {
          return of(userData);
        }

        return this.resolveInitialPartner(userData).pipe(
          concatMap((partner: Partner | undefined) => this.store.select(getSessionData).pipe(
            take(1),
            tap((latestSessionData) => {
              if (partner && !latestSessionData?.partner_id) {
                this.store.dispatch(setPartner({ partner }));
              }
            }),
            map(() => userData),
          )),
        );
      }),
    );
  }

  private resolveInitialPartner(userData: User) {
    const requestOne = this.partnersService.getPartnersV2Observable(PartnerType.AIRLINE);
    const requestTwo = this.partnersService.getPartnersV2Observable(PartnerType.DISTRIBUTOR);
    const requestThree = this.partnersService.getPartnersV2Observable(PartnerType.PLATFORM);

    return forkJoin([requestOne, requestTwo, requestThree]).pipe(
      map(([r1, r2, r3]) => [...r1, ...r2, ...r3]),
      map((partnersList: Partner[]) => this.findInitialPartner(userData, partnersList))
    );
  }

  private findInitialPartner(userData: User, partnersList: Partner[]): Partner | undefined {
    const orgAccess = userData.org_access;
    if (orgAccess?.length && partnersList?.length) {
      if (userData.role === RoleType.OBSERVER) {
        return partnersList.filter(p => !p.carrier_code).find(p => !!p.name && orgAccess.includes(p.name));
      }
      return partnersList.filter(p => !!p.carrier_code).find(p => !!p.name && orgAccess.includes(p.name));
    }

    if (userData.partner_code?.length && userData.partner_code !== 'ATP') {
      return partnersList.find(p => p.carrier_code === userData.partner_code);
    }

    return undefined;
  }
}
